import { AnyAction, combineReducers, configureStore } from '@reduxjs/toolkit';
import { configReducer } from 'redux/config';
import { logout, sessionReducer } from 'redux/session';
import { creditInvoiceManuallyModalReducer } from 'views/Pages/invoices/modals/CreditInvoiceManuallyModalState';
import { invoicesReducer } from 'views/Pages/invoices/invoicesGlobalState';
import { refundCardPaymentModalReducer } from 'views/Pages/payments/modals/RefundCardPaymentModalState';
import { refundCloudPosPaymentModalReducer } from 'views/Pages/payments/modals/RefundCloudPosPaymentModalState';
import { refundSwishPaymentModalReducer } from 'views/Pages/payments/modals/RefundSwishPaymentModalState';
import { sessionsPageReducer } from 'redux/sessionsPage';
import { settlementsReducer } from 'views/Pages/settlements/settlementsGlobalState';
import { createSubscriptionReducer } from 'views/Pages/subscriptions/CreateSubscriptionPageState';
import { subscriptionsReducer } from 'views/Pages/subscriptions/subscriptionsGlobalState';
import { updateSubscriptionReducer } from 'views/Pages/subscriptions/UpdateSubscriptionPageState';
import { customersReducer } from 'redux/customers';
import { loginBankIdReducer } from 'redux/loginBankId';
import { loginCredentialsReducer } from 'redux/loginCredentials';
import { invoiceActionsReducer } from 'actions/invoices/invoiceActionsState';
import { registrationPageReducer } from 'redux/registrationPage';
import { invoiceActionPairOffModalReducer } from 'views/Pages/invoices/modals/PairOffModalState';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { invoiceAggregationsReducer } from 'views/Pages/invoiceAggregations/invoiceAggregationsGlobalState';
import { bankFilePaymentActionSplitMapPayinModalReducer } from 'views/Pages/payments/bankfile/dialogs/SplitMapPayinDialog/SplitMapPayinDialogState';
import { addMappingModalReducer } from 'views/Pages/payments/bankfile/modals/add-mapping/AddMappingModalState';
import { createInvoiceReducer } from 'views/Pages/invoices/CreateInvoicePageState';
import { copyInvoiceCustomerReducer } from 'views/Pages/invoices/modals/CopyInvoiceCustomerModalState';
import { invoicePreviewPdfModalReducer } from 'views/Pages/invoices/modals/InvoicePreviewPdfModalState';
import { paymentTerminalsReducer } from 'views/Pages/payment-terminals/paymentTerminalsGlobalState';
import { createPaymentRequestPageReducer } from '../views/Pages/paymentrequests/CreatePaymentRequestPageState';
import { paymentRequestReducer } from '../views/Pages/paymentrequests/paymentRequestsGlobalState';
import { sendPaymentRequestsActionsReducer } from '../views/Pages/paymentrequests/SendPaymentRequestState';
import { manualCreditUsageModalReducer } from '../views/Pages/invoices/modals/ManualCreditUsageModalState';
import { manualPaymentModalReducer } from '../views/Pages/invoices/modals/ManualPaymentModalState';
import { paymentsReducer } from '../views/Pages/payments/paymentsGlobalState';
import { creditInvoiceFullyModalReducer } from '../views/Pages/invoices/modals/CreditInvoiceFullyModalState';
import { invoiceAggregationActionsReducer } from '../actions/invoiceAggregations/invoiceAggregationsActionsState';
import { paymentDownloadReducer } from '../views/Pages/payments/PaymentDownloadState';
import { bankFilePaymentsReducer } from '../views/Pages/payments/bankfile/bankFilePaymentsGlobalState';
import { markAsManuallyRefundedDialogReducer } from '../views/Pages/payments/bankfile/actions/MarkAsManuallyRefundedDialogState';
import { markAsDoNotMapIgnoredDialogReducer } from '../views/Pages/payments/bankfile/actions/MarkAsDoNotMapIgnoredDialogState';
import { captureCardPaymentModalReducer } from '../views/Pages/payments/modals/CaptureCardPaymentModalState';
import { creditInvoiceManuallyExVatModalReducer } from '../views/Pages/invoices/modals/CreditInvoiceManuallyExVatModalState';
import { updateInvoiceModalReducer } from '../views/Pages/invoices/modals/UpdateInvoiceModalState';
import { releaseCardAuthorizationModalReducer } from '../views/Pages/payments/modals/ReleaseAuthorizationModalState';
import { ignoreRemainingUnmappedAmountDialogReducer } from '../views/Pages/payments/bankfile/actions/IgnoreRemainingUnmappedAmountDialogState';
import { dashboardWidgetsReducer } from '../views/Pages/dashboard/dashboardWidgetsGlobalState';
import { aiChatReducer } from '../components/ai/aiChatSlice';
import { badDebtModalReducer } from '../views/Pages/invoices/modals/BadDebtModalState';
import { userSettingsReducer } from './userSettings';
import snackbarReducer from './snackbar';
import { notificationsReducer } from './notifications';
import { notificationSubscriptionsReducer } from './notificationSubscriptions';
import { aiAgreementsReducer } from './aiAgreements';

const appReducer = combineReducers({
  ai: combineReducers({
    chat: aiChatReducer,
    agreements: aiAgreementsReducer,
  }),
  dashboardWidgets: dashboardWidgetsReducer,
  bankFilePayments: combineReducers({
    global: bankFilePaymentsReducer,
    actions: combineReducers({
      IgnoreRemainingUnmappedAmountDialog: ignoreRemainingUnmappedAmountDialogReducer,
      MarkAsDoNotMapIgnoredDialog: markAsDoNotMapIgnoredDialogReducer,
      MarkAsManuallyRefundedDialog: markAsManuallyRefundedDialogReducer,
    }),
  }),
  bankFilePaymentActionSplitMapPayinModal: bankFilePaymentActionSplitMapPayinModalReducer,
  bankFilePaymentActionAddMappingModal: addMappingModalReducer,
  config: configReducer,
  customers: customersReducer,
  invoices: combineReducers({
    global: invoicesReducer,
    actions: combineReducers({
      CreditInvoiceFullyModal: creditInvoiceFullyModalReducer,
      CreditInvoiceManuallyModal: creditInvoiceManuallyModalReducer,
      CreditInvoiceManuallyExVatModal: creditInvoiceManuallyExVatModalReducer,
      UpdateInvoiceModal: updateInvoiceModalReducer,
      PairOffModal: invoiceActionPairOffModalReducer,
      ManualCreditUsageModal: manualCreditUsageModalReducer,
      ManualPaymentModal: manualPaymentModalReducer,
      BadDebtModal: badDebtModalReducer,
      InvoicePreviewPdfModal: invoicePreviewPdfModalReducer,
    }),
    createInvoice: createInvoiceReducer,
    copyInvoiceCustomer: copyInvoiceCustomerReducer,
  }),
  invoiceAggregations: invoiceAggregationsReducer,
  invoiceAggregationActions: invoiceAggregationActionsReducer,
  invoiceActions: invoiceActionsReducer,
  login: combineReducers({
    bankId: loginBankIdReducer,
    credentials: loginCredentialsReducer,
  }),
  payments: combineReducers({
    actions: combineReducers({
      CaptureCardPaymentModal: captureCardPaymentModalReducer,
      RefundCardPaymentModal: refundCardPaymentModalReducer,
      RefundCloudPosPaymentModal: refundCloudPosPaymentModalReducer,
      RefundSwishPaymentModal: refundSwishPaymentModalReducer,
      ReleaseCardAuthorizationModal: releaseCardAuthorizationModalReducer,
    }),
    download: paymentDownloadReducer,
  }),
  paymentRequests: combineReducers({
    global: paymentRequestReducer,
    CreatePaymentRequestPage: createPaymentRequestPageReducer,
    actions: combineReducers({
      sendPaymentRequest: sendPaymentRequestsActionsReducer,
    }),
  }),
  paymentsPage: paymentsReducer,
  registrationPage: registrationPageReducer,
  session: sessionReducer,
  sessionsPage: sessionsPageReducer,
  settlements: settlementsReducer,
  subscriptions: combineReducers({
    global: subscriptionsReducer,
    createSubscription: createSubscriptionReducer,
    updateSubscription: updateSubscriptionReducer,
  }),
  paymentTerminals: combineReducers({
    global: paymentTerminalsReducer,
  }),
  notifications: notificationsReducer,
  notificationSubscriptions: notificationSubscriptionsReducer,
  userSettings: userSettingsReducer,
  snackbar: snackbarReducer,
});

const rootReducer = (state: ReturnType<typeof appReducer> | undefined, action: AnyAction) => {
  if (action.type === logout.fulfilled.type) {
    // only retain state.config on logout
    return appReducer(
      {
        ...appReducer(undefined, { type: undefined }),
        ...(state && { config: state.config }),
      },
      { type: undefined },
    );
  }
  return appReducer(state, action);
};

export type MerchantAdminState = ReturnType<typeof store.getState>;
export type MerchantAdminDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<MerchantAdminDispatch>();
export const useAppSelector: TypedUseSelectorHook<MerchantAdminState> = useSelector;
const store = configureStore({ reducer: rootReducer });
export default store;
